/**
 * ScrollToTopButton (요구사항 #277-5, #314)
 * 일정 거리 이상 스크롤 시 우측 하단에 ↑ 화살표. 클릭 시 smooth scroll 로 최상단.
 *
 * 메인 스크롤이 window 가 아니라 자체 div 컨테이너인 경우 containerRef 전달.
 * containerRef 없으면 window.scrollY 모니터.
 */
import { useEffect, useState } from "react";
import { ChevronUp } from "lucide-react";

interface Props {
  /** 표시 임계 (px). default 400 */
  threshold?: number;
  /** 메인 스크롤이 별도 컨테이너 div 인 경우 그 ref 전달 */
  containerRef?: React.RefObject<HTMLElement | null>;
}

export default function ScrollToTopButton({ threshold = 400, containerRef }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = containerRef?.current;
    if (el) {
      const onScroll = () => setVisible(el.scrollTop > threshold);
      onScroll();
      el.addEventListener("scroll", onScroll, { passive: true });
      return () => el.removeEventListener("scroll", onScroll);
    }
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold, containerRef]);

  const handleClick = () => {
    const el = containerRef?.current;
    if (el) el.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="페이지 상단으로 이동"
      className="fixed bottom-6 right-6 md:right-[244px] z-[110] flex h-11 w-11 items-center justify-center rounded-full bg-sky-600/95 text-white shadow-lg hover:bg-sky-700 active:scale-95 transition-all backdrop-blur-sm"
    >
      <ChevronUp className="h-6 w-6" />
    </button>
  );
}
